import type { SimulationRoomSegment } from "../routes/simulationRoomTypes"

export interface SimulationRoomTurn {
  key: string
  speaker: string
  text: string
  /** The first segment's position, for interleaving with transcript events. */
  orderSeq: number
  startOffsetSeconds?: number
  endOffsetSeconds?: number
  isFinal: boolean
}

/**
 * Collapses recognized speech into turns.
 *
 * Speech arrives one segment per phrase as recognition finalizes it, so a single
 * spoken turn shows up as several fragments. Consecutive segments from the same
 * speaker are one turn. Replay dedup happens upstream, keyed by segment id
 * (the backend resends its buffer verbatim, so a segment id is never reused for
 * different content) — this function trusts its input is already deduped and
 * does not try to infer duplicates from text, which would risk dropping a
 * distinct segment that legitimately repeats an earlier word or phrase.
 */
export function groupSegmentsIntoTurns(
  segments: SimulationRoomSegment[],
): SimulationRoomTurn[] {
  const turns: SimulationRoomTurn[] = []

  for (const segment of segments) {
    const text = segment.text.trim()
    if (!text) {
      continue
    }

    const current = turns[turns.length - 1]
    if (current && current.speaker === segment.speaker) {
      current.text = `${current.text} ${text}`
      current.endOffsetSeconds =
        segment.endOffsetSeconds ?? current.endOffsetSeconds
      current.isFinal = segment.isFinal
      continue
    }

    turns.push({
      key: segment.segmentId,
      speaker: segment.speaker,
      text,
      orderSeq: segment.orderSeq,
      startOffsetSeconds: segment.startOffsetSeconds,
      endOffsetSeconds: segment.endOffsetSeconds,
      isFinal: segment.isFinal,
    })
  }

  return turns
}

/** `m:ss` from an offset in seconds, for a transcript interval. */
export function formatOffset(seconds: number | undefined): string {
  const safe =
    seconds !== undefined && Number.isFinite(seconds) && seconds > 0
      ? seconds
      : 0
  const minutes = Math.floor(safe / 60)
  const remainder = Math.floor(safe % 60)
  return `${minutes}:${String(remainder).padStart(2, "0")}`
}
